import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, ShieldAlert, Play, PenLine } from "lucide-react";
import { SourceBadge } from "@/components/v3/SourceBadge";
import { LoadingState } from "@/components/v3/States";

const ST = {
  pass: "bg-emerald-50 text-emerald-700 border-emerald-300", manual_pass: "bg-sky-50 text-sky-700 border-sky-300",
  fail: "bg-red-50 text-[#b3122a] border-[#b3122a]", pending: "bg-slate-100 text-slate-500 border-slate-200",
};

export function VerificationTab({ id, reload, canRun }) {
  const [v, setV] = useState(null);
  const [ov, setOv] = useState(null);
  const load = () => api.get(`/v3/loanfile/${id}/verification`).then((r) => setV(r.data)).catch(() => setV({ items: [], summary: {} }));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);
  const err = (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
  const run = (i) => api.post(`/v3/loanfile/${id}/verification/run`, { code: i.code, target: i.target })
    .then(() => { toast.success(`${i.code} — MOCK pass recorded`); load(); reload(); }).catch(err);
  const runAll = async () => {
    for (const i of v.items.filter((x) => x.status === "pending")) await api.post(`/v3/loanfile/${id}/verification/run`, { code: i.code, target: i.target }).catch(err);
    toast.success("All pending checks run (MOCK)"); load(); reload();
  };
  const override = () => api.post(`/v3/loanfile/${id}/verification/override`, ov)
    .then(() => { toast.success("Override recorded"); setOv(null); load(); reload(); }).catch(err);
  if (!v) return <LoadingState label="Loading verification…" />;
  const s = v.summary;
  const groups = v.items.reduce((a, i) => { (a[i.group_label] = a[i.group_label] || []).push(i); return a; }, {});
  return (
    <div data-testid="verification-tab">
      <div className="bg-white border border-slate-200 p-5 mb-4 flex flex-wrap items-center gap-6">
        {[["Checks", s.total], ["Passed", s.pass + (s.manual_pass || 0)], ["Failed", s.fail], ["Pending", s.pending]].map(([k, n]) => (
          <div key={k}><div className="ceo-label">{k}</div><div className={`ceo-fig text-3xl ${k === "Failed" && n ? "text-[#b3122a]" : ""}`} data-testid={`verif-${k.toLowerCase()}`}>{n}</div></div>
        ))}
        <div className="ml-auto flex items-center gap-2 flex-wrap">
          <SourceBadge source="MOCK" />
          <span className="text-[11px] text-slate-500 max-w-[280px]">{v.note}</span>
          {canRun && <button data-testid="verif-run-all" onClick={runAll} className="rf-btn-primary"><Play size={13} strokeWidth={1.5} /> Run all pending</button>}
        </div>
      </div>
      {s.fail > 0 && <div className="mb-4 border border-[#b3122a] bg-red-50 px-4 py-2.5 text-[12px] text-[#b3122a] font-semibold flex items-center gap-2" data-testid="verif-warning"><ShieldAlert size={14} /> {s.fail} check(s) failed — soft gate: Credit may proceed but the committee summary will flag this.</div>}
      {Object.entries(groups).map(([g, items]) => (
        <div key={g} className="bg-white border border-slate-200 p-5 mb-3">
          <div className="ceo-label mb-2">{g}</div>
          {items.map((i) => (
            <div key={i.code + i.target} className="flex items-center gap-3 border-b border-slate-100 py-2 text-sm" data-testid={`verif-item-${i.code}`}>
              <span className={`text-[10px] font-bold uppercase border px-1.5 py-0.5 w-24 text-center ${ST[i.status]}`} data-testid={`verif-status-${i.code}`}>{i.status.replace("_", " ")}</span>
              <span className="flex-1"><span className="font-semibold text-slate-800">{i.label}</span>{i.target && <span className="text-slate-500"> · {i.target}</span>}
                <div className="text-[11px] text-slate-400">{i.provider} · {i.provider_configured ? "keys saved — adapter pending" : "MOCK"}{i.run_at ? ` · ${new Date(i.run_at).toLocaleString()} by ${i.run_by}` : ""}{i.override_reason ? ` · reason: ${i.override_reason}` : ""}</div></span>
              {canRun && <>
                <button data-testid={`verif-run-${i.code}`} onClick={() => run(i)} className="rf-btn-ghost"><Play size={12} strokeWidth={1.5} /> Run</button>
                <button data-testid={`verif-override-${i.code}`} onClick={() => setOv({ code: i.code, target: i.target, status: "manual_pass", reason: "" })} className="rf-btn-ghost"><PenLine size={12} strokeWidth={1.5} /> Override</button>
              </>}
            </div>
          ))}
        </div>
      ))}
      {ov && (
        <div className="bg-white border-2 border-[#111111] p-5" data-testid="override-form">
          <div className="font-semibold text-sm mb-2 flex items-center gap-2"><ShieldCheck size={14} /> Manual override — {ov.code} {ov.target}</div>
          <div className="flex gap-2 flex-wrap">
            <select data-testid="override-status" className="rf-input max-w-[160px]" value={ov.status} onChange={(e) => setOv({ ...ov, status: e.target.value })}>
              <option value="manual_pass">Manual pass</option><option value="fail">Fail</option><option value="pending">Reset to pending</option>
            </select>
            <input data-testid="override-reason" className="rf-input flex-1" placeholder="Reason (audit-logged, min 5 chars)" value={ov.reason} onChange={(e) => setOv({ ...ov, reason: e.target.value })} />
            <button data-testid="override-save" onClick={override} className="rf-btn-primary">Save</button>
            <button onClick={() => setOv(null)} className="rf-btn-ghost">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}
